import { cn } from '@utils/cn';
import React, { createElement, forwardRef, Ref } from 'react';

import styles from './typography.module.css';

export type HeadingLevelType = 'display' | 'h1' | 'h2' | 'h3' | 'h4';
export type HeadingWeightType = 'bold' | 'regular';
export const TYPOGRAPHY_COLORS = [
  'primary',
  'secondary',
  'active',
  'positive',
  'danger',
  'info',
  'inverse',
  'inherit',
] as const;
export type TypographyColorType = (typeof TYPOGRAPHY_COLORS)[number];
export type HeadingElementType = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p';

export type HeadingProps = Omit<React.HTMLAttributes<HTMLElement>, 'color'> & {
  /** Visual role. Also picks the element, unless `as` overrides it. */
  level?: HeadingLevelType;
  /** `regular` is the Subheading style. */
  weight?: HeadingWeightType;
  color?: TypographyColorType;
  /** Render a different element, e.g. an `h2` that looks like an H1. */
  as?: HeadingElementType;
};

const DEFAULT_ELEMENT: Record<HeadingLevelType, HeadingElementType> = {
  display: 'h1',
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
};

export const Heading = forwardRef(
  (
    {
      as,
      children,
      className,
      color = 'primary',
      level = 'h2',
      weight = 'bold',
      ...props
    }: HeadingProps,
    ref: Ref<HTMLElement>,
  ) =>
    createElement(
      as ?? DEFAULT_ELEMENT[level],
      {
        ...props,
        ref,
        className: cn(
          styles[`UiTypography__color__${color}`],
          styles[`UiHeading__level__${level}`],
          styles[`UiHeading__weight__${weight}`],
          className,
        ),
      },
      children,
    ),
);

Heading.displayName = 'Heading';
